import { Box, Card, CardContent, Tooltip, Typography } from "@mui/material";
import { TrendingUp, TrendingDown, TrendingFlat } from "@mui/icons-material";
import { KpiMetric } from "../types";
import { ReactNode } from "react";

interface KpiCardProps extends KpiMetric {
  color?: string;
  icon?: ReactNode;
  tooltip?: string;
}

const KpiCard = ({ label, value, delta, deltaType = "neutral", color, icon, tooltip }: KpiCardProps) => {
  const deltaColor =
    deltaType === "positive" ? "#00B140" : deltaType === "negative" ? "#DC143C" : "#8A8D8F";

  const DeltaIcon =
    deltaType === "positive" ? TrendingUp : deltaType === "negative" ? TrendingDown : TrendingFlat;

  const card = (
    <Card
      elevation={0}
      sx={{
        border: "1px solid",
        borderColor: "#E8EBF0",
        borderLeft: color ? `4px solid ${color}` : "1px solid #E8EBF0",
        borderRadius: "12px",
        height: "100%",
        background: "#ffffff",
        boxShadow: "0 2px 8px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.04)",
        transition: "all 0.25s ease",
        "&:hover": {
          boxShadow: "0 8px 24px rgba(0,0,0,0.10), 0 2px 6px rgba(0,0,0,0.06)",
          transform: "translateY(-2px)",
        },
      }}
    >
      <CardContent sx={{ p: { xs: 1.5, lg: "10px" }, "&:last-child": { pb: { xs: 1.5, lg: "10px" } } }}>
        <Box display="flex" alignItems="center" gap={1}>
          {icon && (
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: "10px",
                background: color ? `linear-gradient(135deg, ${color}18, ${color}08)` : "linear-gradient(135deg, #f0f0f0, #e8e8e8)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                "& svg": { fontSize: 18, color: color || "#666" },
              }}
            >
              {icon}
            </Box>
          )}
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant="caption"
              noWrap
              sx={{ color: "#64748B", fontWeight: 600, fontSize: "0.6rem", textTransform: "uppercase", letterSpacing: "0.5px", display: "block" }}
            >
              {label}
            </Typography>
            <Box display="flex" alignItems="center" justifyContent="space-between" gap={0.5}>
              <Typography variant="h5" fontWeight={800} sx={{ color: "#0F172A", lineHeight: 1.2, fontSize: { xs: "0.95rem", lg: "1rem" } }}>
                {value}
              </Typography>
              {delta && (
                <Box display="flex" alignItems="center" gap={0.25} sx={{ flexShrink: 0 }}>
                  <DeltaIcon sx={{ fontSize: 14, color: deltaColor }} />
                  <Typography variant="caption" sx={{ color: deltaColor, fontWeight: 600, fontSize: "0.7rem" }}>
                    {delta}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  return tooltip ? <Tooltip title={tooltip} arrow placement="top"><div>{card}</div></Tooltip> : card;
};

export default KpiCard;
